import { z } from "zod";
import { zDate } from "utils/zDate";
import { isAfter, isBefore, isEqual } from "date-fns";
import { isDateValid } from "utils/toDate";
import { regexCodeSociete } from "utils/regex";
import {
  CHAMP_REQUIS,
  MESSAGE_ERROR_DATE_DEBUT_EFFET_SUPERIEURE_DATE_FIN,
  MESSAGE_ERROR_DATE_DEBUT_HABILITATION_MINIMUM,
  MESSAGE_ERROR_DATE_DEBUT_HABILITATION_SUPERIEURE_DATE_FIN,
  MESSAGE_ERROR_DATE_FIN_HABILITATION_INFERIEURE_DATE_DEBUT,
  MESSAGE_ERROR_DATE_FIN_HABILITATION_MINIMUM,
  MESSAGE_ERROR_DATE_FIN_INFERIEURE_DATE_DEBUT,
  MESSAGE_ERROR_FORMAT_CODE_SOCIETE,
} from "utils/constantes";
import { getFieldDateValue } from "utils/getElementValue";
import { Perimetre } from "api/auth";

export const labelPermimetre = {
  G: "Gestion globale",
  S: "Gestion sinistre",
  C: "Consultation",
} as const;

export const labelPermimetreFiltre = {
  Tous: "Tous",
  G: "Gestion globale",
  S: "Gestion sinistre",
  C: "Consultation",
} as const;

export const habilitationSchema = z.object({
  idHabilitation: z.string(),
  nomAssureur: z.string(),
  codeAssureur: z.string(),
  nomIntermediaire: z.string(),
  codeGestionnaire: z.string(),
  perimetre: z.nativeEnum(Perimetre),
  debutHabilitation: z.string(),
  finHabilitation: z.string().nullish(),
  synchroFva: z.boolean(),
});
export type Habilitation = z.infer<typeof habilitationSchema>;

type API_LIST<T> = {
  totalItems: number;
  habilitations: T[];
  totalPages: number;
  currentPage: number;
};

export type HabilitationApi = API_LIST<Habilitation>;

export const habilitationQuerySchema = z
  .object({
    codeGestionnaire: z
      .string()
      .regex(regexCodeSociete, {
        message: MESSAGE_ERROR_FORMAT_CODE_SOCIETE,
      })
      .optional(),
    codeAssureur: z
      .string()
      .regex(regexCodeSociete, {
        message: MESSAGE_ERROR_FORMAT_CODE_SOCIETE,
      })
      .optional(),
    debutHabilitation: zDate().refine(
      (value) => {
        const finValue = getFieldDateValue("finHabilitation");
        return finValue ? isBefore(value, finValue) : true;
      },
      { message: MESSAGE_ERROR_DATE_DEBUT_EFFET_SUPERIEURE_DATE_FIN },
    ),
    finHabilitation: zDate().refine(
      (value) => {
        const debutValue = getFieldDateValue("debutHabilitation");
        return debutValue ? isAfter(value, debutValue) : true;
      },
      { message: MESSAGE_ERROR_DATE_FIN_INFERIEURE_DATE_DEBUT },
    ),
    perimetre: z.nativeEnum(Perimetre),
    synchroFVA: z.string(),
    page: z.number(),
    size: z.number(),
    habActif: z.boolean(),
  })
  .partial();
export type HabilitationQuery = z.infer<typeof habilitationQuerySchema>;

export const habilitationFormSchema = z.object({
  idHabilitation: z.string(),
  codeGestionnaire: z
    .string()
    .regex(regexCodeSociete, MESSAGE_ERROR_FORMAT_CODE_SOCIETE)
    .optional(),
  debutHabilitation: zDate({
    min: {
      getValue: () => new Date(),
      message: MESSAGE_ERROR_DATE_DEBUT_HABILITATION_MINIMUM,
    },
  }).optional(),
  finHabilitation: zDate({
    min: {
      getValue: () => new Date(),
      message: MESSAGE_ERROR_DATE_FIN_HABILITATION_MINIMUM,
    },
  }).optional(),
  perimetre: z.nativeEnum(Perimetre).optional(),
});

type HabilitationForm = z.infer<typeof habilitationFormSchema>;

const debutHabilitationIsbeforeOrEqualFinHabilitation = (
  data: Omit<HabilitationForm, "idHabilitation">,
) => {
  return data.debutHabilitation &&
    isDateValid(data.debutHabilitation) &&
    data.finHabilitation &&
    isDateValid(data.finHabilitation)
    ? isBefore(data.debutHabilitation, data.finHabilitation) ||
        isEqual(data.debutHabilitation, data.finHabilitation)
    : true;
};

const addContratHabilitationFormSchemaRules = (model: z.ZodObject<any>): z.ZodEffects<any> => {
  return model.superRefine((data, ctx) => {
    // Mettre les champs obligatoires seulement dans le superRefine pour s'assurerque tous les champrs requis soient contrôlées en même temps
    if (!data.codeGestionnaire) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CHAMP_REQUIS,
        path: ["codeGestionnaire"],
      });
    }
    if (!data.debutHabilitation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CHAMP_REQUIS,
        path: ["debutHabilitation"],
      });
    }
    if (!data.perimetre) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CHAMP_REQUIS,
        path: ["perimetre"],
      });
    }
    if (!debutHabilitationIsbeforeOrEqualFinHabilitation(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MESSAGE_ERROR_DATE_DEBUT_HABILITATION_SUPERIEURE_DATE_FIN,
        path: ["debutHabilitation"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MESSAGE_ERROR_DATE_FIN_HABILITATION_INFERIEURE_DATE_DEBUT,
        path: ["finHabilitation"],
      });
    }
  });
};

export const habilitationFormCreateSchema = addContratHabilitationFormSchemaRules(
  habilitationFormSchema.omit({ idHabilitation: true }),
);

export type HabilitationFormCreate = z.infer<typeof habilitationFormCreateSchema>;

export const habilitationFormEditSchema = addContratHabilitationFormSchemaRules(
  habilitationFormSchema.omit({ debutHabilitation: true }).merge(
    z.object({
      debutHabilitation: zDate({}),
    }),
  ),
);

export type HabilitationFormEdit = z.infer<typeof habilitationFormEditSchema>;

export const habilitationFormDisableSchema = habilitationFormSchema.pick({
  idHabilitation: true,
});
export type HabilitationFormDisable = z.infer<typeof habilitationFormDisableSchema>;
